import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: "/dashboard", label: "Available Leads", icon: "ri-store-2-line", exact: true },
  { path: "/dashboard/purchased", label: "Purchased Leads", icon: "ri-shopping-bag-line" },
  { path: "/dashboard/billing", label: "Billing", icon: "ri-bank-card-line" },
  { path: "/dashboard/settings", label: "Settings", icon: "ri-settings-3-line" },
  { path: "/dashboard/contract", label: "Contract", icon: "ri-file-text-line" },
];

interface OnboardingStatus {
  contractSigned: boolean;
  paymentAdded: boolean;
  loaded: boolean;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingStatus>({ contractSigned: true, paymentAdded: true, loaded: false });
  const [installerInitials, setInstallerInitials] = useState("JS");

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("installers")
        .select("contract_signed, stripe_payment_method_id, contact, company")
        .eq("id", user.id)
        .maybeSingle();
      if (data) {
        setOnboarding({
          contractSigned: !!data.contract_signed,
          paymentAdded: !!data.stripe_payment_method_id,
          loaded: true,
        });
        const name = data.contact || data.company || "";
        const parts = name.trim().split(" ");
        setInstallerInitials(
          parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : name.slice(0, 2).toUpperCase() || "JS"
        );
      } else {
        setOnboarding({ contractSigned: true, paymentAdded: true, loaded: true });
      }
    };
    load();
  }, []);

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      {/* Top header */}
      <header className="bg-white border-b border-gray-100 px-4 md:px-6 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              className="md:hidden w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#16a34a] rounded-md flex items-center justify-center">
                <i className="ri-sun-line text-white text-xs"></i>
              </div>
              <span className="font-bold text-gray-900 text-base tracking-tight hidden sm:block">
                SolarLead<span className="text-[#16a34a]">Source</span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 bg-[#16a34a] rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-green-700">3 new leads available</span>
            </div>
            <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center cursor-pointer">
              <span className="text-white text-xs font-bold">{installerInitials}</span>
            </div>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                navigate("/login");
              }}
              className="text-xs text-gray-500 hover:text-gray-700 transition cursor-pointer whitespace-nowrap hidden sm:block"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-0 left-0 z-20 w-60 bg-white border-r border-gray-100 pt-16 pb-6 flex flex-col
          transform transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static md:pt-0 md:z-auto
        `}>
          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                  isActive(item.path, item.exact)
                    ? "bg-green-50 text-[#16a34a]"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                {item.label}
                {item.path === "/dashboard" && (
                  <span className="ml-auto bg-[#16a34a] text-white text-xs px-1.5 py-0.5 rounded-full font-semibold">3</span>
                )}
              </Link>
            ))}
          </nav>

          <div className="px-3 border-t border-gray-100 pt-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-green-800 mb-1">Need help?</p>
              <p className="text-xs text-green-700 mb-3">Our team is here to help you get the most from your leads.</p>
              <a href="mailto:support@solarleadsource.com" className="text-xs font-medium text-[#16a34a] hover:underline">
                Contact Support <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-10 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 p-4 md:p-8">
          {/* Onboarding progress banner */}
          {onboarding.loaded && (!onboarding.contractSigned || !onboarding.paymentAdded) && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-amber-600 text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-amber-800 mb-1">Complete your setup to start buying leads</p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${onboarding.contractSigned ? "bg-green-100 text-green-700" : "bg-white border border-amber-300 text-amber-700"}`}>
                      <i className={onboarding.contractSigned ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}></i>
                      Sign Contract
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${onboarding.paymentAdded ? "bg-green-100 text-green-700" : "bg-white border border-amber-300 text-amber-700"}`}>
                      <i className={onboarding.paymentAdded ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}></i>
                      Add Payment Method
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(!onboarding.contractSigned ? "/contract" : "/payment-setup")}
                  className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition cursor-pointer whitespace-nowrap"
                >
                  Continue Setup
                </button>
              </div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
